import { useEffect, useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Input } from '@/shared/components/ui/input';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { cn } from '@/shared/lib/cn';
import { formatMoney } from './EstadoFinancieroReport';
import type { EfDetalleRow } from './api';

/**
 * Listado de movimientos detrás de una celda. Lo comparten el drill-down del
 * Estado Financiero y el de Evolución: cambia de dónde salen las filas, no
 * cómo se muestran, así que el buscador, el pie con el total y el manejo de
 * carga/error viven una sola vez.
 */
export function MovimientosDetalleDialog({
  abierto,
  titulo,
  subtitulo,
  rows,
  moneda,
  cargando,
  error,
  onClose,
}: {
  abierto: boolean;
  titulo: string;
  subtitulo?: string;
  rows: EfDetalleRow[];
  moneda: string;
  cargando: boolean;
  error: boolean;
  onClose: () => void;
}) {
  const [busqueda, setBusqueda] = useState('');
  // Se limpia al cambiar de celda: el texto de una no tiene por qué servir
  // en la siguiente.
  useEffect(() => setBusqueda(''), [titulo]);

  const filas = useMemo(() => {
    const term = busqueda.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((r) =>
      `${r.descripcion} ${r.cliente} ${r.cuentaContable} ${r.categoria}`
        .toLowerCase()
        .includes(term),
    );
  }, [rows, busqueda]);

  // El total sigue a lo filtrado: si no, el pie contradice lo que se ve.
  const total = useMemo(() => filas.reduce((acc, r) => acc + r.monto, 0), [filas]);

  return (
    <Dialog open={abierto} onOpenChange={(open) => !open && onClose()}>
      {/* `p-0` + padding propio por bloque: así el cuerpo scrollea a lo ancho
          sin que la primera y la última columna queden pegadas al borde. */}
      <DialogContent className="flex max-h-[85vh] w-[min(64rem,95vw)] max-w-none flex-col gap-0 overflow-hidden p-0">
        <DialogHeader className="shrink-0 border-b px-6 py-4">
          <DialogTitle className="pr-8">{titulo}</DialogTitle>
          {!cargando && !error && (
            <p className="text-sm text-muted-foreground">
              {subtitulo ? `${subtitulo} · ` : ''}
              {filas.length === rows.length
                ? `${filas.length} movimientos`
                : `${filas.length} de ${rows.length} movimientos`}
            </p>
          )}
        </DialogHeader>

        {!cargando && !error && (
          <div className="shrink-0 border-b px-6 py-3">
            <div className="relative w-full sm:max-w-sm">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                aria-hidden
              />
              <Input
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                placeholder="Buscar descripción, cliente o cuenta contable"
                aria-label="Buscar en el detalle"
                className="pl-9"
              />
            </div>
          </div>
        )}

        {cargando ? (
          <div className="space-y-2 px-6 py-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} className="h-6 w-full" />
            ))}
          </div>
        ) : error ? (
          <p className="px-6 py-4 text-sm text-destructive">
            No se pudo cargar el detalle.
          </p>
        ) : (
          <>
            <div className="min-h-0 flex-1 overflow-auto">
              <table className="w-full min-w-[52rem] border-separate border-spacing-0 text-sm">
                <thead className="sticky top-0 z-10">
                  <tr className="[&>th]:border-b [&>th]:bg-card [&>th]:px-3 [&>th]:py-2.5 [&>th]:text-xs [&>th]:font-medium [&>th]:uppercase [&>th]:tracking-wide [&>th]:text-muted-foreground">
                    <th className="!pl-6 text-left">Fecha</th>
                    <th className="text-left">Categoría</th>
                    <th className="text-left">Cuenta Contable</th>
                    <th className="text-left">Descripción</th>
                    <th className="text-left">Cliente</th>
                    <th className="!pr-6 text-right">Monto</th>
                  </tr>
                </thead>
                <tbody>
                  {filas.map((r, i) => (
                    <tr
                      key={i}
                      className={cn(
                        'transition-colors hover:bg-primary/5',
                        // Cebra sutil: en listados largos ayuda a seguir la
                        // fila hasta el importe sin perder el renglón.
                        i % 2 === 1 && 'bg-muted/20',
                      )}
                    >
                      <td className="whitespace-nowrap border-b px-3 py-2.5 pl-6 text-muted-foreground">
                        {fechaCorta(r.fecha)}
                      </td>
                      <td className="max-w-[12rem] border-b px-3 py-2.5">
                        <span className="block truncate" title={r.categoria}>
                          {r.categoria || '—'}
                        </span>
                      </td>
                      <td className="max-w-[13rem] border-b px-3 py-2.5">
                        <span className="block truncate" title={r.cuentaContable}>
                          {r.cuentaContable || '—'}
                        </span>
                      </td>
                      <td className="max-w-[20rem] border-b px-3 py-2.5">
                        <span className="block truncate" title={r.descripcion}>
                          {r.descripcion || '—'}
                        </span>
                      </td>
                      <td className="max-w-[12rem] border-b px-3 py-2.5">
                        <span className="block truncate" title={r.cliente}>
                          {r.cliente || '—'}
                        </span>
                      </td>
                      <td
                        className={cn(
                          'whitespace-nowrap border-b px-3 py-2.5 pr-6 text-right font-medium tabular-nums',
                          r.monto < 0 ? 'text-red-600' : 'text-emerald-700',
                        )}
                      >
                        {formatMoney(r.monto, moneda)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Fuera del contenedor scrolleable: el total queda siempre a la
                vista sin depender de `position: sticky` dentro de la tabla. */}
            <div className="flex shrink-0 items-center justify-between border-t bg-muted/30 px-6 py-3 text-sm font-semibold">
              <span>Total</span>
              <span
                className={cn('tabular-nums', total < 0 ? 'text-red-600' : 'text-emerald-700')}
              >
                {formatMoney(total, moneda)}
              </span>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** `2025-09-01` → `01/09/2025`. */
function fechaCorta(iso: string): string {
  const [a, m, d] = iso.slice(0, 10).split('-');
  return `${d}/${m}/${a}`;
}
